import { ChartConfigChartPaneElement, RequireByKey } from '../../types/shared'
import { IndicatorType } from '../constants/common'
import EMA from '../helpers/ema'
import Pane from '../models/pane'
import { drawInVisibleArea } from '../utils/draw_in_visible_area'
import { getParsedIntegersFromPeriodString } from '../utils/helpers'
import Indicator from './indicator'

interface Attrs {
  period: string
}

class MACD extends Indicator<Attrs> {
  static type = IndicatorType.Macd
  static label = 'MACD'

  static getNumOfBarsBuffer({ period }: RequireByKey<ChartConfigChartPaneElement, 'period'>) {
    const [fastPeriod = 0, slowPeriod = 0, signalPeriod = 0] = getParsedIntegersFromPeriodString(period)
    return Math.max(fastPeriod, slowPeriod, signalPeriod)
  }

  macd: number[] = []
  signal: number[] = []
  histogram: number[] = []
  declare fastPeriod: number
  declare slowPeriod: number
  declare signalPeriod: number

  constructor(attrs: Attrs, model: Pane) {
    super(attrs, model)
    if (typeof this.attrs.period === 'string') {
      const [fastPeriod = 0, slowPeriod = 0, signalPeriod = 0] = getParsedIntegersFromPeriodString(this.attrs.period)
      this.fastPeriod = fastPeriod
      this.slowPeriod = slowPeriod
      this.signalPeriod = signalPeriod
    }
  }

  compute() {
    if (this.isComputeNecessary()) {
      this.macd = []
      this.signal = []
      this.histogram = []
      const ema1 = new EMA(this.fastPeriod)
      const ema2 = new EMA(this.slowPeriod)
      const ema3 = new EMA(this.signalPeriod)

      for (let i = 0; i < this.data.close.length; i++) {
        ema1.add(this.data.close[i])
        ema2.add(this.data.close[i])
        const macd = ema1.value - ema2.value
        ema3.add(macd)
        const signal = ema3.value

        this.macd[i] = macd
        this.signal[i] = signal
        this.histogram[i] = macd - signal
      }

      this.lastValue = this.macd.last() ?? null
    }
    const { min, max } =
      this.macd.length > 0 || this.signal.length > 0 || this.histogram.length > 0
        ? this.computeVisibleMinMax(this.macd, this.signal, this.histogram)
        : this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const { IndicatorSettings, ChartSettings } = this.getChartLayoutSettings()
    if (this.data.close.length === 0) return
    const y = Math.round(this.fy(0))
    const w = ChartSettings.center.barWidth + ChartSettings.center.border * 2
    const drawInVisibleAreaProps = {
      quote: this.data,
      scale: this.model.scale,
      leftOffset: this.leftOffset,
      width: this.width,
    }

    context.set('fillStyle', '#79c3e8')
    context.beginPath()
    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.rect(x - ~~(ChartSettings.center.barWidth / 2), y, w, Math.round(this.fy(this.histogram[i]) - y))
      },
    })
    context.fill()

    context.translate(0.5, 0.5)
    context.set('strokeStyle', IndicatorSettings.general.Colors.line)
    context.beginPath()

    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.lineTo(x, Math.round(this.fy(this.macd[i])))
      },
    })
    context.stroke()

    context.set('strokeStyle', IndicatorSettings.general.Colors.signalLine)
    context.beginPath()
    drawInVisibleArea({
      ...drawInVisibleAreaProps,
      drawBarCallback: (i, x) => {
        context.lineTo(x, Math.round(this.fy(this.signal[i])))
      },
    })
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getModalConfig() {
    const fastPeriod = {
      type: 'number',
      label: 'Period',
      name: 'fastPeriod',
      value: this.fastPeriod ?? 12,
      required: true,
      min: 1,
      max: 999999,
    }
    const slowPeriod = {
      type: 'number',
      label: 'Slow',
      name: 'slowPeriod',
      value: this.slowPeriod ?? 26,
      required: true,
      min: 1,
      max: 999999,
    }
    const signalPeriod = {
      type: 'number',
      label: 'Signal',
      name: 'signalPeriod',
      value: this.signalPeriod ?? 9,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [fastPeriod, slowPeriod, signalPeriod],
      inputsErrorMessages: {
        fastPeriod: `${fastPeriod.label} must be a whole number between ${fastPeriod.min} and ${fastPeriod.max}`,
        slowPeriod: `${slowPeriod.label} must be a whole number between ${slowPeriod.min} and ${slowPeriod.max}`,
        signalPeriod: `${signalPeriod.label} must be a whole number between ${signalPeriod.min} and ${signalPeriod.max}`,
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'fastPeriod':
      case 'slowPeriod':
      case 'signalPeriod':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `MACD ${this.fastPeriod},${this.slowPeriod},${this.signalPeriod}`
  }
}

export default MACD
